import React, {
  createContext,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import { server } from "./configs";
import Axios from "axios";
import Swal from "sweetalert2";
import Loading from "./components/Loading";
import NoToken from "./NoToken";

const Context = createContext();

const GlobalContext = ({ children, token }) => {
  const [surveyList, setSurveyList] = useState([]);
  const [userData, setUserData] = useState({});
  const [selfData, setSelfData] = useState({});
  const [progressData, setProgressData] = useState({});
  const [answerData, setAnswerData] = useState([]);
  const [surveyLength, setSurveyLength] = useState({});
  const [reqInfoPages, setReqInfoPages] = useState({});

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isSurveyLoaded, setIsSurveyLoaded] = useState(false);
  const [isProgressLoaded, setIsProgressLoaded] = useState(false);
  const [isSurveyDone, setIsSurveyDone] = useState(false);
  const [isFinishedSaved, setIsFinishedSaved] = useState(false);

  const [error, setError] = useState(false);

  const history = useHistory();

  // Once only: Grab user info from server and set to userData if it is empty
  useEffect(() => {
    if (JSON.stringify(userData) === JSON.stringify({})) {
      const jwt = localStorage.getItem("jwt");

      Axios.get(server + "/user/survey/precondition", {
        headers: { authorization: jwt },
      })
        .then(async ({ data }) => {
          // If required info is empty, admin has incorrectly set institute settings
          // (설문에서 제외된 조사대상의 URL이 활성화되어 있는 경우)
          if (data.requiredInfo.length === 0) {
            history.push("/no-info");
          } else {
            setUserData({ ...userData, ...data });
            setIsDataLoaded(true);
          }
        })
        .catch((e) => {
          console.log(e);

          // If sufficient info has not been set by admin, send user to
          // no info page (조사대상 설정 누락)
          if (e.response.status === 412) {
            history.push("/no-info");
          }
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When isDataLoaded changes: If data has been loaded, check if various data
  // lists that the site needs has been loaded and set. If any specific data list
  // does not exist, get the data from the server and set it.
  useEffect(() => {
    if (isDataLoaded) {
      const jwt = localStorage.getItem("jwt");

      // If there are no surveys set, get data from server
      if (surveyList.length === 0) {
        Axios.get(server + "/user/survey/questionnaire", {
          headers: { authorization: jwt },
        })
          .then(async (response) => {
            // Check if surveys have been set before setting other data
            if (response.data.surveys.length === 0) {
              history.push("/no-info");
            } else {
              setSurveyList(response.data.surveys);
              setIsSurveyLoaded(true);

              // Set all survey lengths
              let lengthList = {};
              let surveyIdx = -1; // Set to -1 to make first one 0
              let surveyLength;
              for (let survey of response.data.surveys) {
                surveyIdx = surveyIdx + 1;
                surveyLength = 0;

                for (let topic of survey.topics) {
                  surveyLength += topic.screens.length;
                }

                lengthList[surveyIdx] = surveyLength;
              }

              setSurveyLength(lengthList);
            }
          })
          .catch((e) => {
            console.log(e);
          });
      } else {
        setIsSurveyLoaded(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoaded]);

  // In GlobalContext to control order and number of requests
  const submitFinish = useCallback(
    (content, pushUrl) => {
      const jwt = localStorage.getItem("jwt");

      Axios.patch(server + "/user/users/me", content, {
        headers: { authorization: jwt },
      })
        .then(() => {
          setIsFinishedSaved(true);

          Axios.get(server + "/user/users/me", {
            headers: { authorization: jwt },
          })
            .then(({ data }) => {
              setSelfData(data);
              history.push(pushUrl);
            })
            .catch((e) => {
              console.log(e);
            });
        })
        .catch((e) => {
          console.log(e);
          history.push("/noToken");
        });
    },
    [history]
  );

  useEffect(() => {
    if (isSurveyDone && !isFinishedSaved) {
      const isNoGift = userData.gov.giftList.length === 0;

      // If there aren't any gifts to give, set user to finished and send
      // directly to final page
      if (isNoGift) {
        // Mark as finished
        const content = {
          giftOption: "NONE", // Passing as none as there must be something saved
          finished: true,
        };
        submitFinish(content, "/token/" + token + "/finished");
      } else {
        const content = {
          giftOption: "NONE",
          finished: true,
        };
        submitFinish(content, `/token/${token}/success`);
      }
    }
  }, [submitFinish, isSurveyDone, isFinishedSaved, userData, token]);

  useEffect(() => {
    const jwt = localStorage.getItem("jwt");

    Axios.get(server + "/user/users/me", {
      headers: { authorization: jwt },
    })
      .then(async (response) => {
        const urlList = ["finished", "success", "done"];
        const isUrlIncluded = urlList.some((substring) =>
          window.location.href.includes(substring)
        );

        if (
          // Send user to error page if user is on one of the last 3 pages
          // and has set giftOption to either DONATE or GET, and set user to
          // error page if user is on any other page with finishedAt set
          (response.data.finishedAt !== null && !isUrlIncluded) ||
          ((response.data.giftOption === "DONATE" ||
            response.data.giftOption === "GET") &&
            isUrlIncluded)
        ) {
          Swal.fire({
            title: "에러",
            confirmButtonText: "닫기",
            confirmButtonColor: "#00aeef",
            text: "이미 참여하기를 완료한 사용자입니다.",
            heightAuto: false,
          });
          localStorage.removeItem("jwt");
          localStorage.removeItem("token");
          setError(true);
        } else {
          // Check if surveys have been set before setting other data
          if (response.data.progress.surveys.length !== 0) {
            setSelfData(response.data);

            // If progressData has been updated, set new data
            if (
              progressData.notAnsweredTotalCnt !==
              response.data.progress.notAnsweredTotalCnt
            ) {
              setProgressData(response.data.progress);
            }

            if (!isProgressLoaded) {
              setIsProgressLoaded(true);
            }

            // Set answer data for all surveys
            let answerList = [];
            for (let survey of response.data.progress.surveys) {
              for (let topic of survey.topics) {
                for (let screen of topic.screens) {
                  for (let question of screen.questions) {
                    if (
                      question.answerType === "DEGREE" &&
                      question.answers.length !== 0
                    ) {
                      answerList.push({
                        answers: [{ degree: question.answers[0].degree }],
                        questionId: question.id,
                      });
                    } else if (
                      question.answerType === "SHORT_TEXT" &&
                      question.answers.length !== 0
                    ) {
                      answerList.push({
                        answers: [
                          {
                            description: question.answers[0].description,
                          },
                        ],
                        questionId: question.id,
                      });
                    } else if (
                      question.answerType === "MUL_CHOICE" &&
                      question.answers.length !== 0
                    ) {
                      let mulAnsList = [];

                      for (let answer of question.answers) {
                        mulAnsList.push({
                          optionId: answer.optionId,
                          description: answer.description,
                        });
                      }

                      answerList.push({
                        answers: mulAnsList,
                        questionId: question.id,
                      });
                    }
                  }
                }
              }
            }
            setAnswerData(answerList);
          }
        }
      })
      .catch((e) => {
        console.log(e);
        setError(true);
        let errorMsg = "";

        if (e.response.status === 401) {
          errorMsg = "만료된 사용자입니다.";
        } else if (e.response.status === 403) {
          errorMsg = "이미 조사가 종료되었습니다.";
        } else if (e.response.status === 404) {
          errorMsg = "잘못된 파라미터이거나 없는 값입니다.";
        } else if (e.response.status === 500) {
          errorMsg = "서버 문제가 발생했습니다. 관리자에게 연락 부탁드립니다.";
        }

        if (
          e.response.status === 401 ||
          e.response.status === 403 ||
          e.response.status === 404 ||
          e.response.status === 500
        ) {
          localStorage.removeItem("jwt");
          localStorage.removeItem("token");
          Swal.fire({
            title: "에러",
            confirmButtonText: "닫기",
            confirmButtonColor: "#00aeef",
            text: errorMsg,
            heightAuto: false,
          });
        }
      });
  }, [progressData, isProgressLoaded]);

  // Check what user info the survey needs to send user to appropriate page
  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      const requiredInfo = userData.requiredInfo;

      const needsAgree =
        requiredInfo.findIndex((field) => field.includes("agree")) >= 0;
      const needsInfoOne =
        requiredInfo.findIndex((field) => field.includes("gender")) >= 0 ||
        requiredInfo.findIndex((field) => field.includes("year")) >= 0 ||
        requiredInfo.findIndex((field) => field.includes("grade")) >= 0 ||
        requiredInfo.findIndex((field) => field.includes("dong")) >= 0 ||
        requiredInfo.findIndex((field) => field.includes("liveWith")) >= 0 ||
        requiredInfo.findIndex((field) => field.includes("myChildLiveWith")) >=
          0 ||
        requiredInfo.includes("relationWithChild") ||
        requiredInfo.includes("achievement") ||
        requiredInfo.includes("jobType") ||
        requiredInfo.includes("certificate") ||
        requiredInfo.includes("isEducated") ||
        requiredInfo.includes("workedAt");
      const needsInfoTwo =
        requiredInfo.findIndex((field) =>
          field.includes("localSatisfaction")
        ) >= 0 ||
        requiredInfo.findIndex((field) => field.includes("lifeSatisfaction")) >=
          0 ||
        requiredInfo.findIndex((field) => field.includes("happiness")) >= 0;

      setReqInfoPages({ needsAgree, needsInfoOne, needsInfoTwo });
    }
  }, [userData]);

  const firstSurvType = useMemo(
    () =>
      surveyList[0]?.surveyType === "아동척도"
        ? "a"
        : surveyList[0]?.surveyType === "영향평가"
        ? "b"
        : "c",
    [surveyList]
  );

  const store = {
    token,
    userData,
    setUserData,
    selfData,
    setSelfData,
    surveyList,
    surveyLength,
    progressData,
    setIsProgressLoaded,
    answerData,
    reqInfoPages,
    firstSurvType,
    setIsSurveyDone,
  };

  useEffect(() => {
    // console.log("app data", userData);
  }, [userData]);

  useEffect(() => {
    // console.log("app selfData", selfData);
  }, [selfData]);

  useEffect(() => {
    // console.log("app surveyList", surveyList);
  }, [surveyList]);

  useEffect(() => {
    // console.log("app answerData", answerData);
  }, [answerData]);

  useEffect(() => {
    // console.log("app surveyLength", surveyLength);
  }, [surveyLength]);

  useEffect(() => {
    // console.log("app progressData", progressData);
  }, [progressData]);

  useEffect(() => {
    // console.log("app reqInfoPages", reqInfoPages);
  }, [reqInfoPages]);

  return error ? (
    <NoToken />
  ) : !isDataLoaded || !isSurveyLoaded ? (
    <Loading />
  ) : (
    <Context.Provider value={store}>{children}</Context.Provider>
  );
};

export { Context };
export default GlobalContext;
